import _ from 'lodash';
import React from 'react';
import postal from 'postal';
import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import ErrorBoundary from '../../../shared/error-boundary';
import {
  SortableFilterableTableHeader,
  SORT_DIRECTION,
  filterSimpleColumn,
  sortSimpleColumn,
} from '../../../shared/sortable-filterable-table-header';
import mutate from '../../../shared/mutate';
import Partnership from './partnership';
import EditPartnershipModal from './edit-partnership-modal';
import { getAll } from '../../../services/lwadmin/partnership/partnership-service';

function filterPartnerships(partnerships, state) {
  let filteredPartnerships = _(partnerships);

  filteredPartnerships = filterSimpleColumn(filteredPartnerships, 'name', state);
  filteredPartnerships = filteredPartnerships.filter(x => !state.inactiveFilter || x.active);

  return filteredPartnerships.value();
}

function sortPartnerships(partnerships, state) {
  let sortedPartnerships = _(partnerships);

  sortedPartnerships = sortSimpleColumn(sortedPartnerships, 'name', state);

  return sortedPartnerships.value();
}

function filterAndSortPartnerships(partnerships, state) {
  return sortPartnerships(filterPartnerships(partnerships, state), state);
}

export default class PartnershipsTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      /* eslint-disable react/no-unused-state */
      allPartnerships: [],
      partnerships: [],
      nameSortDirection: SORT_DIRECTION.unsorted,
      nameFilter: '',
      inactiveFilter: true,
      /* eslint-ensable react/no-unused-state */
    };
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'lwadmin.partnerships.commands',
      topic: 'create-partnership',
      callback: () => this.createPartnership(),
    });

    this.refresh();
  }

  handleFilterOutInactivePartnershipsChange(e) {
    const inactiveFilter = !e.target.checked;

    this.setState(oldState => mutate(oldState, state => {
      state.inactiveFilter = inactiveFilter;
      state.partnerships = filterAndSortPartnerships(state.allPartnerships, state);
    }));
  }

  createPartnership() {
    this.editPartnershipModal.present();
  }

  refresh() {
    this.loading.start();
    getAll()
      .then(result => {
        this.setState(oldState => mutate(oldState, state => {
          state.allPartnerships = _(result.data.partnerships)
            .map(p => new Partnership(p))
            .sortBy(x => x.name)
            .value();
          state.partnerships = filterAndSortPartnerships(state.allPartnerships, state);
        }));
      })
      .catch(error => defaultOnErrorHandler(error, 'Error loading partnerships'))
      .finally(() => this.loading.stop());
  }

  editPartnership(e, partnership) {
    e && e.preventDefault && e.preventDefault();

    this.editPartnershipModal.present(partnership.id);
  }

  toggleActivePartnership(e, partnership) {
    e && e.preventDefault && e.preventDefault();

    const found = _.find(this.state.partnerships, { id: partnership.id });
    if (found) {
      found[partnership.active ? 'deactivate' : 'activate']().then(() => {
        this.setState(oldState => mutate(oldState, state => {
          state.partnerships = filterAndSortPartnerships(state.allPartnerships, state);
        }));
      });
    }
  }

  sortName(direction) {
    this.setState(oldState => mutate(oldState, state => {
      state.nameSortDirection = direction;
      state.partnerships = filterAndSortPartnerships(state.allPartnerships, state);
    }));
  }

  filterName(filter) {
    this.setState(oldState => mutate(oldState, state => {
      state.nameFilter = filter;
      state.partnerships = filterAndSortPartnerships(state.allPartnerships, state);
    }));
  }

  render() {
    return (
      <ErrorBoundary>
        <div className="lwadmin-partnerships-table">
          <Loading ref={c => (this.loading = c)}>
            <p className="form-group">
              <label htmlFor="include-inactive">
                <input
                  id="include-inactive"
                  type="checkbox"
                  checked={!this.state.inactiveFilter}
                  onChange={e => this.handleFilterOutInactivePartnershipsChange(e)}
                />
                Include inactive partnerships
              </label>
            </p>
            <table className="table table-hover">
              <thead>
                <tr>
                  <SortableFilterableTableHeader
                    onSort={direction => this.sortName(direction)}
                    onFilter={filter => this.filterName(filter)}
                  >
                    Partnership Name
                  </SortableFilterableTableHeader>
                  <th>Partnership level</th>
                  <th>No. Contacts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {this.state.partnerships.map(p => (
                  <tr key={p.id}>
                    <td>
                      {
                        p.active
                          ? p.name
                          : (
                            <span>
                              <strike>{p.name}</strike>
                              {' (inactive)'}
                            </span>
                          )
                      }
                    </td>
                    <td>{p.partnership_type}</td>
                    <td>
                      {`${p.sent_contact_count}/${p.total_contact_count}`}
                    </td>
                    <td className="table-tools">
                      <ul>
                        <li><a href={`/lwadmin/partnership/${p.id}/wires`}>View</a></li>
                        <li><a href="#" onClick={e => this.editPartnership(e, p)}>Edit</a></li>
                        <li>
                          {
                            p.dashboard_url
                              ? (
                                <a
                                  href={p.dashboard_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Dashboard
                                </a>
                              )
                              : <span>Dashboard</span>
                          }
                        </li>
                        <li>
                          <a
                            href="#"
                            onClick={e => this.toggleActivePartnership(e, p)}
                          >
                            {p.active ? 'Deactivate' : 'Activate'}
                          </a>
                        </li>
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Loading>
          <EditPartnershipModal
            ref={c => (this.editPartnershipModal = c)}
            partnershipDidSave={() => this.refresh()}
          />
        </div>
      </ErrorBoundary>
    );
  }
}
